import { Measures } from "../../../telemetry/Measures";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { addSystemThemeChangedEventListener, getSystemTheme, removeSystemThemeChangedEventListener } from "../../externalApi/ThemeApi";
import log from "loglevel";
import { getUriForResource } from "../registrar/registrarHelpers";
//
// customized img element for placeholder image
// this extends img so we can know when its replaced by the widget
//
const defaultWidgetTimeoutSeconds = 120;
const feedTimeoutSeconds = 20;
export class PlaceholderImageElement extends HTMLImageElement {
    constructor() {
        super();
        this._elapsedTime = 0; // Time passed since the first timeout for feed widget starts to run
        this._widgetInstance = null;
        this._timeout = null;
        this._boundStateCallback = null;
        this._boundLifecycleCallback = null;
        this._boundSizeCallback = null;
        this._boundThemeCallback = null;
        this._startRuntime = performance.now();
        this._isDarkTheme = null;
        this._placeholderSize = null;
    }
    static createPlaceholderImage(widgetInstance, id, isDarkTheme) {
        Trace.logEvent(dashboard, "WebAppPlaceholderCreating", 0 /* verbose */, {
            appId: widgetInstance.widgetRegistration.appId,
            providerId: widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: widgetInstance.widgetDefinitionId,
            widgetInstanceId: widgetInstance.widgetId,
        });
        const placeholderImageElement = document.createElement("img", {
            is: "img-placeholder",
        });
        placeholderImageElement.init(widgetInstance, id, isDarkTheme);
        return placeholderImageElement;
    }
    init(widgetInstance, id, isDarkTheme) {
        var _a, _b, _c, _d;
        this.widgetInstance = widgetInstance;
        this.id = "placeholder:" + id;
        this.setAttribute("role", "presentation");
        this._isDarkTheme = isDarkTheme;
        let lifecycleApi = widgetInstance.tryGetApi("lifecycle", 1, 1);
        if (lifecycleApi) {
            this._boundLifecycleCallback = this.widgetLifecycleEvent.bind(this);
            lifecycleApi.addEventListener(this._boundLifecycleCallback);
        }
        const widgetSizeApi = widgetInstance.tryGetApi("sizes", 1, 1);
        if (widgetSizeApi) {
            this._boundSizeCallback = this.sizeChangeEvent.bind(this);
            widgetSizeApi.addEventListener(this._boundSizeCallback);
        }
        this._boundThemeCallback = this.onThemeChanged.bind(this);
        addSystemThemeChangedEventListener(this._boundThemeCallback);
        if (widgetInstance.widgetDefinitionId != "com.msn.desktopfeed") {
            let size = widgetInstance.getWidgetProperty("size");
            if (!size) {
                size = widgetInstance.widgetRegistration.preferredSize;
            }
            if (widgetInstance.widgetRegistration.placeholderImage[0].src.length < 1) {
                Trace.logEvent(dashboard, "WebAppPlaceholderNoImage", 3 /* error */, {
                    appId: (_a = this._widgetInstance) === null || _a === void 0 ? void 0 : _a.widgetRegistration.appId,
                    providerId: (_b = this._widgetInstance) === null || _b === void 0 ? void 0 : _b.widgetRegistration.providerId,
                    widgetDefinitionId: (_c = this._widgetInstance) === null || _c === void 0 ? void 0 : _c.widgetDefinitionId,
                    widgetInstanceId: (_d = this._widgetInstance) === null || _d === void 0 ? void 0 : _d.widgetId,
                });
                log.error("Placeholder image src empty [", widgetInstance.widgetRegistration.id, "]");
            }
            else {
                this._placeholderSize = size;
                this.findPlaceholderImage(this.widgetInstance);
                if (!this.src) {
                    // If we couldn't find a match, just use the first one available
                    Trace.logEvent(dashboard, "WebAppPlaceholderNoMatchFound", 3 /* error */, {
                        widgetId: widgetInstance.widgetId,
                    });
                    this.src = getUriForResource(widgetInstance.widgetRegistration.id, widgetInstance.widgetRegistration.placeholderImage[0].src);
                }
            }
        }
        const widgetTimeoutSeconds = this._widgetInstance && this._widgetInstance.widgetId === "com.msn.desktopfeed"
            ? feedTimeoutSeconds
            : defaultWidgetTimeoutSeconds;
        // In case the widget fails to remove the placeholder and nothing signals an error,
        // automatically send widget to error state with a timeout
        this._timeout = global.setTimeout(() => this.reportLoadFailed(), widgetTimeoutSeconds * 1000);
        this._startRuntime = performance.now();
        this._boundStateCallback = this.widgetStateChangeCallback.bind(this);
        widgetInstance.addStateChangeListener(this._boundStateCallback);
    }
    widgetStateChangeCallback(instance, previousState, currentState) {
        var _a;
        if (currentState === "normal" || currentState === "error") {
            // If we are transitioning from any state to normal or error the placeholder shoud be removed from the DOM if it still exists
            if (this.isConnected) {
                (_a = this.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(this);
            }
        }
    }
    sizeChangeEvent(eventName, size) {
        if (eventName === "onSizeChangeEnd" && this.widgetInstance) {
            this._placeholderSize = size;
            this.findPlaceholderImage(this.widgetInstance);
        }
    }
    reportLoadFailed() {
        if (this._widgetInstance) {
            Trace.logEvent(dashboard, "WebAppPlaceholderReportLoadFailed", 0 /* verbose */, {
                appId: this._widgetInstance.widgetRegistration.appId,
                providerId: this._widgetInstance.widgetRegistration.providerId,
                widgetDefinitionId: this._widgetInstance.widgetDefinitionId,
                widgetInstanceId: this._widgetInstance.widgetId,
            });
            this._widgetInstance.widgetErrorStateReason = "LoadTimeout";
            this._widgetInstance.widgetErrorCode = 258; // WAIT_TIMEOUT
            this._widgetInstance.widgetState = "error";
        }
    }
    async widgetLifecycleEvent(eventName) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (eventName == "suspending") {
            this._elapsedTime += Math.max(performance.now() - this._startRuntime, 0);
            if (this.isConnected) {
                // On suspend clear the timeout
                this.clearTimeout();
                Measures.logEvent(dashboard, "WidgetInstancePlaceholderTimeoutSuspended", 16777216 /* productAndServicePerformance */, {
                    appId: (_a = this._widgetInstance) === null || _a === void 0 ? void 0 : _a.widgetRegistration.appId,
                    providerId: (_b = this._widgetInstance) === null || _b === void 0 ? void 0 : _b.widgetRegistration.providerId,
                    widgetDefinitionId: (_c = this._widgetInstance) === null || _c === void 0 ? void 0 : _c.widgetDefinitionId,
                    widgetInstanceId: (_d = this._widgetInstance) === null || _d === void 0 ? void 0 : _d.widgetId,
                    widgetState: (_e = this._widgetInstance) === null || _e === void 0 ? void 0 : _e.widgetState,
                });
            }
        }
        else if (eventName == "resumed") {
            this._startRuntime = performance.now();
            if (this.isConnected) {
                // On resume restart the timeout
                this.clearTimeout();
                if (this.widgetInstance && this.widgetInstance.widgetId === "com.msn.desktopfeed") {
                    const remainingRunningTimeinMs = Math.max(feedTimeoutSeconds * 1000 - this._elapsedTime, 0);
                    this._timeout = global.setTimeout(() => this.reportLoadFailed(), remainingRunningTimeinMs);
                }
                else {
                    this._timeout = global.setTimeout(() => this.reportLoadFailed(), defaultWidgetTimeoutSeconds * 1000);
                }
                Measures.logEvent(dashboard, "WidgetInstancePlaceholderTimeoutResumed", 16777216 /* productAndServicePerformance */, {
                    appId: (_f = this._widgetInstance) === null || _f === void 0 ? void 0 : _f.widgetRegistration.appId,
                    providerId: (_g = this._widgetInstance) === null || _g === void 0 ? void 0 : _g.widgetRegistration.providerId,
                    widgetDefinitionId: (_h = this._widgetInstance) === null || _h === void 0 ? void 0 : _h.widgetDefinitionId,
                    widgetInstanceId: (_j = this._widgetInstance) === null || _j === void 0 ? void 0 : _j.widgetId,
                    widgetState: (_k = this._widgetInstance) === null || _k === void 0 ? void 0 : _k.widgetState,
                });
            }
        }
    }
    disconnectedCallback() {
        var _a, _b, _c, _d, _e, _f;
        // element removed from DOM
        // this means that the widget replaced the placeholder or we've hit an error state
        this.clearTimeout();
        let lifecycleApi = (_a = this._widgetInstance) === null || _a === void 0 ? void 0 : _a.tryGetApi("lifecycle", 1, 1);
        if (lifecycleApi && this._boundLifecycleCallback) {
            lifecycleApi.removeEventListener(this._boundLifecycleCallback);
            this._boundLifecycleCallback = null;
        }
        const widgetSizeApi = (_b = this._widgetInstance) === null || _b === void 0 ? void 0 : _b.tryGetApi("sizes", 1, 1);
        if (widgetSizeApi && this._boundSizeCallback) {
            widgetSizeApi.removeEventListener(this._boundSizeCallback);
        }
        removeSystemThemeChangedEventListener(this.onThemeChanged);
        if (this._widgetInstance) {
            if (this._boundStateCallback) {
                this._widgetInstance.removeStateChangeListener(this._boundStateCallback);
                this._boundStateCallback = null;
            }
            Trace.logEvent(dashboard, "WebAppPlaceholderRemoved", 0 /* verbose */, {
                appId: (_c = this._widgetInstance) === null || _c === void 0 ? void 0 : _c.widgetRegistration.appId,
                providerId: (_d = this._widgetInstance) === null || _d === void 0 ? void 0 : _d.widgetRegistration.providerId,
                widgetDefinitionId: (_e = this._widgetInstance) === null || _e === void 0 ? void 0 : _e.widgetDefinitionId,
                widgetInstanceId: (_f = this._widgetInstance) === null || _f === void 0 ? void 0 : _f.widgetId,
            });
            if (this._widgetInstance.widgetState !== "error") {
                // it's possible that the widget state is already in a normal state when the widget is disconnected.
                // In this case, we can leave the widget state as normal and just disconnect the widget instance.
                if (this._widgetInstance.widgetState !== "normal") {
                    log.info("PlaceHolderImageElement[" + this.id + "]: setting widgetState to normal");
                    this._widgetInstance.widgetState = "normal";
                }
                this._widgetInstance = null;
            }
            else {
                log.info("PlaceHolderImageElement[" + this.id + "]: removed during error state");
            }
        }
    }
    get widgetInstance() {
        return this._widgetInstance;
    }
    set widgetInstance(val) {
        this._widgetInstance = val;
    }
    clearTimeout() {
        if (this._timeout) {
            global.clearTimeout(this._timeout);
            this._timeout = null;
        }
    }
    // Changes placeholder mode when user changes mode in between sessions
    async onThemeChanged() {
        let systemColors = await getSystemTheme();
        const systemBackgroundColor = systemColors.substr(0, 6);
        this._isDarkTheme = systemBackgroundColor === "000000";
        if (this.widgetInstance) {
            this.findPlaceholderImage(this.widgetInstance);
        }
    }
    findPlaceholderImage(widgetInstance) {
        // Find the first matching image for the theme and set it as the placeholder
        for (let image of widgetInstance.widgetRegistration.placeholderImage) {
            if (image.size === this._placeholderSize && ((this._isDarkTheme && image.theme === "dark") || (!this._isDarkTheme && image.theme === "light"))) {
                if (widgetInstance.isPlatform && !widgetInstance.widgetRegistration.id.startsWith("com.")) {
                    this.src = image.src;
                }
                else {
                    this.src = getUriForResource(widgetInstance.widgetRegistration.id, image.src);
                }
                break;
            }
        }
    }
}
